/**
 * `/login` — email + password against worker-auth `POST /auth/login` (auth-and-authz.md §1;
 * fixtures: login-*). Honours the contract's outcomes:
 *  - `totp_required`           → /login/totp (the second factor; credentials held in the store
 *                                for the re-POST);
 *  - `totp_enrolment_required` (identity session minted, no factor yet) → /login/totp-enrol;
 *  - `force_password_change`   (breached deferred-HIBP; no session) → /forgot-password;
 *  - authenticated             → the firm picker (/firms) — or, when the sign-in was started
 *                                from an invitation (`?redirect=/invite/<token>`), back to it;
 *                                started from Security settings (`?redirect=/settings/security`,
 *                                a refused two-factor change) → the picker, then back there.
 * The redirect is carried through every step (see router/redirect.ts) — the in-page links to
 * the recovery-code path carry it too, as router links (no full reload, the SPA state and the
 * pending sign-in survive). Errors surface the typed, non-revealing `ApiError.message` (CC-7).
 * Reached from a completed password reset (history state), it shows a one-line note.
 */
import { Link, useNavigate, useRouterState, useSearch } from '@tanstack/react-router';
import { useId, useState, type FormEvent, type JSX } from 'react';

import {
  AuthCard,
  FormError,
  FormStatus,
  PasswordField,
  SubmitButton,
  TextField,
} from '../components/ui.tsx';
import { getErrorMessage } from '../lib/api.ts';
import {
  carriedRedirect,
  inviteTokenOf,
  returnPathOf,
  useAfterSignIn,
} from '../router/redirect.ts';
import { useAuth } from '../store/auth-store.ts';
import { PASSWORD_CHANGED_NOTE, wasPasswordChanged } from './reset-password-screen.tsx';

export function LoginScreen(): JSX.Element {
  const navigate = useNavigate();
  const errorId = useId();
  const afterSignIn = useAfterSignIn();
  const { redirect } = useSearch({ strict: false }) as { redirect?: string };
  const carry = carriedRedirect(redirect);
  const forInvite = inviteTokenOf(redirect) !== null;
  // Sent back from a two-factor change worker-auth refused on a password-only session.
  const forSecurity = returnPathOf(redirect) === '/settings/security';
  // Sent here by a completed password reset (all sessions revoked; sign in with the new one).
  const passwordChanged = useRouterState({ select: (s) => wasPasswordChanged(s.location.state) });
  const signIn = useAuth((s) => s.signIn);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await signIn(email.trim().toLowerCase(), password);
      switch (result.outcome) {
        case 'totp_required':
          await navigate({ to: '/login/totp', search: carry });
          return;
        case 'totp_enrolment_required':
          await navigate({ to: '/login/totp-enrol', search: carry });
          return;
        case 'force_password_change':
          await navigate({ to: '/forgot-password', search: carry });
          return;
        case 'authenticated':
          await afterSignIn(redirect);
          return;
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard
      title="Sign in"
      subtitle={
        forInvite
          ? 'Sign in to the invited account to accept your invitation.'
          : forSecurity
            ? 'Sign in again with your authenticator app (or a recovery code) to manage two-factor authentication.'
            : 'Welcome back — access your examinations.'
      }
    >
      <div style={{ display: 'grid', gap: 16 }}>
        <FormStatus message={passwordChanged ? PASSWORD_CHANGED_NOTE : null} />
        <form
          onSubmit={handleSubmit}
          aria-label="Sign in to ARGUS"
          style={{ display: 'grid', gap: 12 }}
        >
          <TextField
            label="Work email"
            name="email"
            type="email"
            value={email}
            onChange={setEmail}
            autoComplete="username"
            placeholder="you@firm.example"
          />
          <PasswordField
            label="Password"
            name="password"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            {...(error ? { describedBy: errorId } : {})}
          />
          <FormError message={error} id={errorId} />
          <SubmitButton disabled={busy || email === '' || password === ''}>
            {busy ? 'Signing in…' : 'Continue'}
          </SubmitButton>
          <nav
            aria-label="Other ways to sign in"
            style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}
          >
            <Link to="/forgot-password" search={carry}>
              Forgot password?
            </Link>
            <Link to="/login/recovery" search={carry}>
              Use a recovery code
            </Link>
          </nav>
        </form>
      </div>
    </AuthCard>
  );
}
